import {
    all, put, select, take, takeLatest,
} from 'redux-saga/effects';

import { REQUEST_POST_USER_SUCCESS } from './APIUsers';
import {
    REQUEST_GET_ORGA_SUCCESS,
    REQUEST_POST_ORGA_SUCCESS,
} from './APIOrgas';
import { REQUEST_GET_ME_SUCCESS } from './APIMe';

/*
 * Configuration
 */
export const ressourceName = 'TOKEN';
export const ressourceNamePlural = 'TOKENS';

/*
 * Actions
 */
export const REQUEST_POST_TOKEN = `REQUEST_POST_${ressourceName}`;
export const REQUEST_POST_TOKEN_SUCCESS = `REQUEST_POST_${ressourceName}_SUCCESS`;
export const REQUEST_POST_TOKEN_ERROR = `REQUEST_POST_${ressourceName}_ERROR`;

export const REQUEST_POST_SESSION = 'REQUEST_POST_SESSION';
export const REQUEST_POST_SESSION_SUCCESS = 'REQUEST_POST_SESSION_SUCCESS';
export const REQUEST_POST_SESSION_ERROR = 'REQUEST_POST_SESSION_ERROR';

export const REQUEST_POST_FORGOT_PASSWORD = 'REQUEST_POST_FORGOT_PASSWORD';
export const REQUEST_POST_FORGOT_PASSWORD_SUCCESS = 'REQUEST_POST_FORGOT_PASSWORD_SUCCESS';
export const REQUEST_POST_FORGOT_PASSWORD_ERROR = 'REQUEST_POST_FORGOT_PASSWORD_ERROR';

export const REQUEST_POST_CHECK_TOKEN = 'REQUEST_POST_CHECK_TOKEN';
export const REQUEST_POST_CHECK_TOKEN_SUCCESS = 'REQUEST_POST_CHECK_TOKEN_SUCCESS';
export const REQUEST_POST_CHECK_TOKEN_ERROR = 'REQUEST_POST_CHECK_TOKEN_ERROR';

export const REQUEST_POST_CHANGE_PASSWORD = 'REQUEST_POST_CHANGE_PASSWORD';
export const REQUEST_POST_CHANGE_PASSWORD_SUCCESS = 'REQUEST_POST_CHANGE_PASSWORD_SUCCESS';
export const REQUEST_POST_CHANGE_PASSWORD_ERROR = 'REQUEST_POST_CHANGE_PASSWORD_ERROR';

export const REFRESH_ACCESS_TOKEN = 'REFRESH_ACCESS_TOKEN';
export const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN';
export const SET_REFRESH_TOKEN = 'SET_REFRESH_TOKEN';
export const SET_USER_ID = 'SET_USER_ID';
export const SET_ORGA_ID = 'SET_ORGA_ID';

/*
 * Reducer
 */
const initialState = {
    accessToken: window.YP_API ? window.YP_API.ACCESS_TOKEN : null,
    refreshToken: window.YP_API ? window.YP_API.REFRESH_TOKEN : null,
    clientId: YP_CONFIG.api[window.YP_API.SCOPE].CLIENT_ID,
    clientSecret: YP_CONFIG.api[window.YP_API.SCOPE].CLIENT_SECRET,
    scope: window.YP_API ? window.YP_API.SCOPE : null,
    expires_in: 0,
    isLoading: false,
    userId: null,
    orgaId: null,
};

export function AuthenticationReducer(state = initialState, action) {
    switch (action.type) {
        case SET_ACCESS_TOKEN:
            return {
                ...state,
                accessToken: action.payload.accessToken,
            };
        case SET_REFRESH_TOKEN:
            return {
                ...state,
                refreshToken: action.payload.refreshToken,
            };
        case REQUEST_POST_TOKEN:
            return {
                ...state,
                isLoading: true,
            };
        case REQUEST_POST_TOKEN_SUCCESS:
            // Legacy tweak: Handle axios middleware
            window.YP_API.ACCESS_TOKEN = action.payload.data.results.access_token;
            window.YP_API.REFRESH_TOKEN = action.payload.data.results.refresh_token;
            return {
                ...state,
                accessToken: action.payload.data.results.access_token,
                refreshToken: action.payload.data.results.refresh_token,
                expiresIn: action.payload.data.results.expires_in,
                isLoading: false,
            };
        case REQUEST_POST_FORGOT_PASSWORD:
        case REQUEST_POST_SESSION:
            return {
                ...state,
                isLoading: true,
            };
        case REQUEST_POST_FORGOT_PASSWORD_ERROR:
        case REQUEST_POST_FORGOT_PASSWORD_SUCCESS:
        case REQUEST_POST_SESSION_ERROR:
        case REQUEST_POST_SESSION_SUCCESS:
        case REQUEST_POST_TOKEN_ERROR:
            return {
                ...state,
                isLoading: false,
            };
        case REQUEST_POST_USER_SUCCESS:
        case REQUEST_GET_ME_SUCCESS:
            return {
                ...state,
                userId: action.payload.data.results.id,
            };
        case SET_USER_ID:
            return {
                ...state,
                userId: action.payload.userId,
            };
        case REQUEST_GET_ORGA_SUCCESS:
        case REQUEST_POST_ORGA_SUCCESS:
            return {
                ...state,
                orgaId: action.payload.data.results.id,
            };
        case SET_ORGA_ID:
            return {
                ...state,
                orgaId: action.payload.orgaId,
            };
        default:
            return state;
    }
}

/*
 * Action Creators
 */

export function postToken(parameters) {
    let cookies;
    if (parameters.grant_type === 'session_connect') {
        cookies = document.cookie;
    }
    return {
        type: REQUEST_POST_TOKEN,
        payload: {
            request: {
                method: 'POST',
                url: '/token',
                data: parameters,
                header: {
                    cookies,
                },
            },
            extras: {
                identifier: Math.random().toString(32).slice(2),
            },
        },
    };
}

export const postSession = () => ({
    type: REQUEST_POST_SESSION,
    payload: {
        request: {
            method: 'POST',
            url: '/session',
            withCredentials: true, // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials
        },
        extras: {
            identifier: Math.random().toString(32).slice(2),
        },
    },
});

export const postForgotPassword = (parameters) => ({
    type: REQUEST_POST_FORGOT_PASSWORD,
    payload: {
        request: {
            method: 'POST',
            url: '/users/forgot_password',
            data: parameters,
        },
        extras: {
            identifier: Math.random().toString(32).slice(2),
        },
    },
});

export const checkToken = (parameters) => ({
    type: REQUEST_POST_CHECK_TOKEN,
    payload: {
        request: {
            method: 'GET',
            url: `/users/change_password_check_token/${parameters.token}`,
            data: parameters,
        },
        extras: {
            identifier: Math.random().toString(32).slice(2),
        },
    },
});

export const postChangePassword = (parameters) => ({
    type: REQUEST_POST_CHANGE_PASSWORD,
    payload: {
        request: {
            method: 'POST',
            url: '/users/change_password',
            data: parameters,
        },
        extras: {
            identifier: Math.random().toString(32).slice(2),
        },
    },
});

/**
 * Create a SET_ACCESS_TOKEN action
 * @param {string} accessToken
 */
export const setAccessToken = (accessToken) => ({
    type: SET_ACCESS_TOKEN,
    payload: {
        accessToken,
    },
});

/**
 * Create a SET_REFRESH_TOKEN action
 * @param {string} refreshToken
 */
export const setRefreshToken = (refreshToken) => ({
    type: SET_REFRESH_TOKEN,
    payload: {
        refreshToken,
    },
});

/**
 * Create a SET_REFRESH_TOKEN action
 * @param {string} refreshToken
 */
export const refreshAccessToken = (requestInfo) => ({
    type: REFRESH_ACCESS_TOKEN,
    payload: {
        requestWrapper: requestInfo,
    },
});

/**
 * Create a SET_USER_ID action
 * @param {integer} userId
 */
export const setUserId = (userId) => ({
    type: SET_USER_ID,
    payload: {
        userId,
    },
});

/**
 * Create a SET_ORGA_ID action
 * @param {integer} orgaId
 */
export const setOrgaId = (orgaId) => ({
    type: SET_ORGA_ID,
    payload: {
        orgaId,
    },
});

/**
 * Selectors
 */

export const getAccessTokenSelector = (state) => state.Authentication.accessToken;
export const getRefreshTokenSelector = (state) => state.Authentication.refreshToken;
export const getClientIdSelector = (state) => state.Authentication.clientId;
export const getClientSecretSelector = (state) => state.Authentication.clientSecret;
export const getScopeSelector = (state) => state.Authentication.scope;
export const isLoadingSelector = (state) => state.Authentication.isLoading;
export const getUserIdSelector = (state) => state.Authentication.userId;
export const getOrgaIdSelector = (state) => state.Authentication.orgaId;

/**
 * Sagas
 */
function* callRefreshToken(action) {
    const [
        refreshToken,
        clientId,
        clientSecret,
        scope,
        isLoading,
    ] = yield all([
        select(getRefreshTokenSelector),
        select(getClientIdSelector),
        select(getClientSecretSelector),
        select(getScopeSelector),
        select(isLoadingSelector),
    ]);
    if (isLoading) return;

    yield put(postToken({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: clientId,
        client_secret: clientSecret,
        scope,
    }));

    const firstTryAction = yield take([
        REQUEST_POST_TOKEN_ERROR,
        REQUEST_POST_TOKEN_SUCCESS,
    ]);

    if (REQUEST_POST_TOKEN_ERROR === firstTryAction.type) {
        /* eslint-disable-next-line no-alert */
        alert('Votre session a expiré. Veuillez vous reconnecter');
        return;
    }

    if (!action.payload.requestWrapper.extras
     || !action.payload.requestWrapper.extras.actionType) {
        return;
    }
    yield put({
        type: action.payload.requestWrapper.extras.actionType,
        payload: {
            request: action.payload.requestWrapper.request,
            extras: action.payload.requestWrapper.extras,
        },
    });
}

export function* watchRefreshAccessToken() {
    yield takeLatest(REFRESH_ACCESS_TOKEN, callRefreshToken);
}
